<script>
    // This control is designed to be used inside a <td> (to move row) or a <li>.
    // We use a div with role="application" for the handle. This allows us to handle keyboard input for moving an item up and down, and 
    // seems to work well with VoiceOver and NVDA.
    // For a description on how keyboard re-ordering works, set the `ariaDescribedById` to refer to e.g. a div with informative text.

    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';
    import { Keys } from '@itslearning/atlas/keyboard/keys';

    onMount(() => {
        // the handle will be inside a list item <li> or a table row <tr>, we mount some event listeners on the container list item (tr, li)
        listItem = getItem(handle);
        listItem.addEventListener("dragenter", handleDragEnter);
        listItem.addEventListener("dragover", handleDragOver);
        listItem.addEventListener("drop", handleDrop);
        listItem.addEventListener("touchmove", handleTouchDragEnter);
    });

    onDestroy(() => {
		listItem.removeEventListener("dragenter", handleDragEnter);
        listItem.removeEventListener("dragover", handleDragOver);
        listItem.removeEventListener("drop", handleDrop);
        listItem.removeEventListener("touchmove", handleTouchDragEnter);
	});

    /** @param itemId - id that will be sent for events - (detail.itemId) */
    export let itemId = undefined;

    /** @param {String} ariaDescribedById - html id to an element that contains aria information on how to perform keyboard re-ordering  */
    export let ariaDescribedById = undefined;

    let isDragging = false;
    let isKeyMoving = false;
    let handle;
    let listItem;

    const dispatch = createEventDispatcher();
    
    function handleDragEnter(/*event*/) {
        dispatch('dragEnterDropArea', {
            itemId: itemId
        });
    }

    function handleTouchDragEnter(event) {
        if (!isDragging) {
            return;
        }
        const x = event.touches[0].clientX;
        const y = event.touches[0].clientY;
        // we need to check if the element under the touch point is a "droppable" area
        const dropAreaId = getDropAreaId(document.elementFromPoint(x, y));
        if (dropAreaId) {
            dispatch('dragEnterDropArea', {
                itemId: dropAreaId
            });
        }
    }

    function handleDragOver(event) {
        // this is to hide the "stop sign" cursor - also enables dropping
        if (event.preventDefault) {
            event.preventDefault();
        }
    }

    function handleDrop(/*event*/) {
        dispatch('dragDropped', {
            itemId: itemId
        });
    }

    function handleDragStart(event) {
        if (isDragging) {
            return;
        }
        isDragging = true;

        event.dataTransfer.effectAllowed = 'move';

        dispatch('dragStarted', {
            itemId: itemId
        });
    }

    function handleTouchDragStart(event) {
        if (event.preventDefault) {
            event.preventDefault();
        }
        if (isDragging) {
            return;
        }
        isDragging = true;

        dispatch('dragStarted', {
            itemId: itemId
        });
    }

    function handleDragEnd(/*event*/) {
        dispatch('dragEnded');
        
        isDragging = false;
    }

    function handleTouchDragEnd(event) {
        if (event.preventDefault) {
            event.preventDefault();
        }
        dispatch('dragEnded');

        isDragging = false;
    }

    function handleKeyDown(event) {
        switch (normalizeKey(event.key)) {
            case Keys.UP:
                if (isKeyMoving) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch('keyMoveUp', {
                        itemId: itemId
                    });
                }
                break;
            case Keys.DOWN:
                if (isKeyMoving) {
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch('keyMoveDown', {
                        itemId: itemId
                    });
                }
                break;
            case Keys.SPACE:
                event.stopPropagation();
                event.preventDefault();
                isKeyMoving = !isKeyMoving;
                if (isKeyMoving) {
                    dispatch('startKeyMove', {
                        itemId: itemId
                    });
                } else {
                    dispatch('endKeyMove');
                }
                break;
            case Keys.TAB:
            case Keys.ESCAPE:
                if (isKeyMoving) {
                    isKeyMoving = false;
                    event.stopPropagation();
                    event.preventDefault();
                    dispatch('endKeyMove');
                }
                break;
        }
    }

    function getDropAreaId(el) {
        // find container (li or tr) that can contain a drag handle
        if (el.nodeName === 'LI' || el.nodeName === 'TR') {
            const dragHandle = el.querySelector('.proto-drag-handle');
            if (dragHandle) {
                // we retrieve the id of the handle
                const handleItemId = dragHandle.id.replace('drag-handle-', '');
                return typeof(itemId) === 'number' ? Number(handleItemId) : handleItemId;
            }
            return null;
        }
        if (el.parentNode) {
            return getDropAreaId(el.parentNode);
        }
        return null;
    }

    function getItem(el) {
        if (el.nodeName === 'LI' || el.nodeName === 'TR') {
            return el;
        }
        if (el.parentNode) {
            return getItem(el.parentNode);
        }
        console.error("unable to find <li> or <tr> element as container for the DragHandle");
        return null;
    }

</script>

<!-- tabindex must be set for this div, otherwise users cannot reach it with keyboard navigation - it has role=application -->
<div
    bind:this={handle}
    tabindex="0"
    draggable={!isDragging}
    role="application" 
    id="drag-handle-{itemId}"
    class="proto-drag-handle"
    aria-describedby="{ariaDescribedById}"
    on:dragstart={handleDragStart}
    on:dragend={handleDragEnd}
    on:drop={handleDrop}
    on:keydown={handleKeyDown}
    on:touchstart={handleTouchDragStart}
    on:touchend={handleTouchDragEnd}
>
</div>
